import React, { useState, useEffect, useCallback } from 'react';
import { format, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameMonth, isSameDay, isAfter, isBefore, addMonths, subMonths, isToday, isSameWeek, parseISO, startOfDay, differenceInDays } from 'date-fns';
import { fr, enUS, ar } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import availabilityService from '../../services/availabilityService';
import api from '../../services/api';
import { AVAILABILITY_STATUS, AVAILABILITY_GROUPS } from '../../constants/availabilityStatus';

import './AvailabilityCalendar.css';

const AvailabilityCalendar = ({ listing, listingId, onDateRangeSelect, onDateRangeChange, selectedStartDate, selectedEndDate }) => {
  const { t, i18n } = useTranslation('booking');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availabilities, setAvailabilities] = useState([]);
  const [fullyBookedDates, setFullyBookedDates] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [selectingStartDate, setSelectingStartDate] = useState(true);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [selectionError, setSelectionError] = useState(null);

  // Contraintes de durée issues des booking_settings
  const isNightsMode = listing?.booking_settings?.date_range_mode === 'nights';
  const minDuration = parseInt(listing?.booking_settings?.min_duration, 10) || null;
  const maxDuration = parseInt(listing?.booking_settings?.max_duration, 10) || null;

  // Calculer la durée entre deux dates : END-EXCLUSIVE pour les deux modes
  // 22 -> 23 = 1 jour / 1 nuit
  // IMPORTANT: utiliser differenceInDays (date-fns) et non l'arithmétique en ms brute,
  // car les jours DST (ex: dernier dimanche de mars) durent 23h → Math.floor(23h/24h)=0
  // ce qui faisait traiter J+1 comme non sélectionnable sur les nuits de passage heure d'été.
  const computeDuration = (start, end) => {
    return differenceInDays(end, start); // DST-safe, calendaire
  };

  const durationUnitLabel = isNightsMode 
      ? t('settings.dateRangeMode.nights', 'nuitée(s)')
        : t('settings.dateRangeMode.days', 'jour(s)');

  // Get the appropriate date-fns locale based on current language (support en-US, fr-FR, etc.)
  const dateLocale = (i18n && i18n.language && i18n.language.startsWith('fr')) ? fr
    : (i18n && i18n.language && i18n.language.startsWith('ar')) ? ar
    : enUS;

  // Get weekday names from translations
  const getWeekdayNames = () => {
    return [
      t('sidebar.calendar.weekdays.monday'),
      t('sidebar.calendar.weekdays.tuesday'),
      t('sidebar.calendar.weekdays.wednesday'),
      t('sidebar.calendar.weekdays.thursday'),
      t('sidebar.calendar.weekdays.friday'),
      t('sidebar.calendar.weekdays.saturday'),
      t('sidebar.calendar.weekdays.sunday')
    ];
  };

  // Charger les disponibilités du listing (périodes bloquées + dates complètes)
  const loadAvailabilities = useCallback(async () => {
    const actualListingId = listingId || listing?.id;
    
    if (!actualListingId) {
      return;
    }
    
    setLoading(true);
    try {
      // Calculer la plage de dates à charger (3 mois)
      const startDate = startOfMonth(subMonths(currentMonth, 1));
      const endDate = endOfMonth(addMonths(currentMonth, 1));
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');
      
      // Charger en parallèle : périodes bloquées (owner) + dates complètes (réservations)
      const [availResponse, bookedResponse] = await Promise.all([
        availabilityService.getListingAvailability(actualListingId, startStr, endStr),
        api.get('/reservations/fully-booked-dates', {
          params: { listing_id: actualListingId, start_date: startStr, end_date: endStr }
        }).catch(err => {
          console.warn('⚠️ [AvailabilityCalendar] Could not fetch fully booked dates:', err.message);
          return { data: { data: { fully_booked_dates: [] } } };
        })
      ]);
      
      setAvailabilities(availResponse.availabilities || []);
      
      // Créer un Set pour un lookup O(1)
      const bookedDates = bookedResponse.data?.data?.fully_booked_dates || [];
      setFullyBookedDates(new Set(bookedDates));
      
      console.log(`📅 [AvailabilityCalendar] Loaded ${bookedDates.length} fully booked dates for ${actualListingId}`);
    } catch (error) {
      console.error('❌ [AvailabilityCalendar] Error loading availabilities:', error);
      setAvailabilities([]);
      setFullyBookedDates(new Set());
    } finally {
      setLoading(false);
    }
  }, [listingId, listing?.id, currentMonth]);

  useEffect(() => {
    loadAvailabilities();
  }, [loadAvailabilities]);

  // Vérifier si une date est complètement réservée (stock épuisé par les réservations)
  // Vérifier si une date est occupée par des réservations (stock épuisé OU bloc auto-créé par reservation)
  // Source de vérité : reservation_id !== null dans les périodes BLOCKED
  const isDateFullyBooked = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    // Source 1 : endpoint /reservations/fully-booked-dates (stock épuisé)
    if (fullyBookedDates.has(dateStr)) return true;
    // Source 2 : périodes BLOCKED avec reservation_id (auto-créées par confirmation réservation)
    return availabilities.some(avail =>
      isDateInAvailPeriod(date, avail) &&
      avail.status === AVAILABILITY_STATUS.BLOCKED &&
      avail.reservation_id !== null && avail.reservation_id !== undefined
    );
  };

  // Tester si une date tombe dans une période de listing_availability
  // End-exclusive pour les deux modes : available_to est le premier jour LIBRE
  const isDateInAvailPeriod = (date, avail) => {
    const availStart = parseISO(avail.available_from);
    const availEnd = parseISO(avail.available_to);
    return date >= availStart && date < availEnd; // End-exclusive pour les deux modes
  };

  // Vérifier si une date est disponible
  // Non disponible = occupée par une réservation OU explicitement BLOCKED par le owner
  const isDateAvailable = (date) => {
    if (isDateFullyBooked(date)) return false;
    if (isDateBlocked(date)) return false;
    return true;
  };

  // Vérifier si une date est bloquée MANUELLEMENT par le owner (pas par une réservation)
  // Source de vérité : BLOCKED sans reservation_id (reservation_id === null)
  const isDateBlocked = (date) => {
    return availabilities.some(avail =>
      isDateInAvailPeriod(date, avail) &&
      avail.status === AVAILABILITY_STATUS.BLOCKED &&
      (avail.reservation_id === null || avail.reservation_id === undefined)
    );
  };

  // Vérifier si une date est explicitement marquée comme disponible
  const isDateExplicitlyAvailable = (date) => {
    return availabilities.some(avail =>
      isDateInAvailPeriod(date, avail) && avail.status === AVAILABILITY_STATUS.AVAILABLE
    );
  };

  // Vérifier si une date est dans le passé
  const isDateInPast = (date) => {
    const today = startOfDay(new Date());
    const checkDate = startOfDay(date);
    return isBefore(checkDate, today);
  };

  // FIX8: Vérifier si un jour est fermé selon opening_hours du listing
  // opening_hours utilise ISO weekday: 1=Lundi ... 7=Dimanche
  // date-fns getDay: 0=Dimanche, 1=Lundi ... 6=Samedi → convertir
  const isDayClosed = (date) => {
    const openingHours = listing?.booking_settings?.opening_hours;
    // Si pas d'opening_hours définis → ouvert 7j/7
    if (!Array.isArray(openingHours) || openingHours.length === 0) return false;
    // Convertir getDay (0=dim) → ISO weekday (1=lun, 7=dim)
    const jsDay = date.getDay(); // 0=dim
    const isoDay = jsDay === 0 ? 7 : jsDay;
    return !openingHours.some(h => h.day === isoDay);
  };

  // Vérifier si une date peut être sélectionnée (disponible, pas dans le passé, pas un jour fermé)
  const isDateSelectable = (date) => {
    return isDateAvailable(date) && !isDateInPast(date) && !isDayClosed(date);
  };

  // Gestion des clics sur les dates
  const handleDateClick = (date) => {
    // Si sélection de DATE DE DÉBUT : doit être disponible et pas dans le passé
    if (selectingStartDate) {
      if (!isDateSelectable(date)) {
        return;
      }
      // Sélection de la date de début
      const callback = onDateRangeChange || onDateRangeSelect;
      callback && callback(date, null);
      setSelectingStartDate(false);
    } else {
      // Si sélection de DATE DE FIN (checkout) : peut être "fully-booked" en end-exclusive
      // MAIS jamais un jour bloqué manuellement par le propriétaire
      if (isDateInPast(date)) {
        return;
      }

      // Un jour bloqué manuellement par le owner ne peut JAMAIS servir de date de fin
      if (isDateBlocked(date)) {
        return;
      }

      if (isBefore(date, selectedStartDate)) {
        // Si la date de fin est avant le début, recommencer la sélection
        const callback = onDateRangeChange || onDateRangeSelect;
        callback && callback(date, null);
        setSelectingStartDate(false);
      } else {
        // Calculer la durée selon le mode (nuitées/journées)
        const duration = computeDuration(selectedStartDate, date);
        
        // Validation: minimum 1 jour/nuit
        if (duration < 1) {
          return;
        }

        // Validation: min_duration
        if (minDuration && duration < minDuration) {
          setSelectionError(t('sidebar.calendar.constraints.minDuration', { min: minDuration, unit: durationUnitLabel }));
          return;
        }

        // Validation: max_duration
        if (maxDuration && duration > maxDuration) {
          setSelectionError(t('sidebar.calendar.constraints.maxDuration', { max: maxDuration, unit: durationUnitLabel }));
          return;
        }

        setSelectionError(null);
        
        // Vérifier que toutes les dates dans la plage sont disponibles
        const isRangeAvailable = checkDateRangeAvailable(selectedStartDate, date);
        if (isRangeAvailable) {
          const callback = onDateRangeChange || onDateRangeSelect;
          callback && callback(selectedStartDate, date);
          setSelectingStartDate(true);
        } else {
          // Si la plage n'est pas entièrement disponible, recommencer
          const callback = onDateRangeChange || onDateRangeSelect;
          callback && callback(date, null);
          setSelectingStartDate(false);
        }
      }
    }
  };

  // Vérifier qu'une plage de dates est entièrement disponible (et pas de jour fermé)
  // End-exclusive pour les deux modes : endDate est le premier jour LIBRE
  const checkDateRangeAvailable = (startDate, endDate) => {
    if (!startDate || !endDate) return true;
    
    let currentDate = new Date(startDate);
    const lastOccupiedDate = addDays(endDate, -1); // End-exclusive pour les deux modes
    
    while (currentDate <= lastOccupiedDate) {
      if (!isDateAvailable(currentDate) || isDayClosed(currentDate)) {
        return false;
      }
      currentDate = addDays(currentDate, 1);
    }
    return true;
  };

  // Vérifier si une date est dans la plage sélectionnée
  const isDateInRange = (date) => {
    if (!selectedStartDate) return false;
    if (!selectedEndDate && !hoveredDate) return false;
    
    const endDate = selectedEndDate || hoveredDate;
    return date >= selectedStartDate && date <= endDate;
  };

  // Navigation entre les mois
  const goToPreviousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  // Générer les jours du calendrier
  const generateCalendarDays = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = [];
    let day = startDate;

    while (day <= endDate) {
      days.push(new Date(day));
      day = addDays(day, 1);
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  // Trouver la première date fully-booked (réservation) après une date donnée
  // Seules les dates épuisées par des réservations peuvent servir de checkout (end-exclusive)
  // Les dates bloquées manuellement par le owner ne sont JAMAIS des checkout valides
  const getFirstBlockedDateAfterStart = (startDate) => {
    if (!startDate) return null;
    
    let checkDate = addDays(startDate, 1);
    const monthEnd = endOfMonth(currentMonth);
    
    while (checkDate <= monthEnd) {
      if (!isDateAvailable(checkDate)) {
        // Seuls les jours épuisés par des réservations peuvent être une date de fin (end-exclusive)
        if (isDateFullyBooked(checkDate)) {
          return new Date(checkDate);
        }
        // Jour bloqué manuellement → pas de checkout possible ici
        return null;
      }
      checkDate = addDays(checkDate, 1);
    }
    return null;
  };

  // Obtenir la classe CSS pour une date
  const getDateClassName = (date) => {
    const classes = ['calendar-day'];
    
    if (!isSameMonth(date, currentMonth)) {
      classes.push('calendar-day--other-month');
    }
    
    if (isToday(date)) {
      classes.push('calendar-day--today');
    }
    
    if (isSameDay(date, selectedStartDate)) {
      classes.push('calendar-day--start-date');
    }
    
    if (isSameDay(date, selectedEndDate)) {
      classes.push('calendar-day--end-date');
    }
    
    if (isDateInRange(date)) {
      classes.push('calendar-day--in-range');
    }
    
    // Nouvelle logique métier : 
    // - Passé = non sélectionnable (gris)
    // - Complet = stock épuisé par réservations (rouge/barré)
    // - Bloqué = explicitement BLOCKED par owner (rouge)
    // - Fermé = jour non ouvré (gris)
    // - Disponible explicite = AVAILABLE (vert foncé)
    // - Disponible par défaut = pas de période ou pas bloqué (vert clair)
    if (isDateInPast(date)) {
      classes.push('calendar-day--past');
    } else if (isDateFullyBooked(date)) {
      classes.push('calendar-day--fully-booked');
      // En end-exclusive, colorer en ambre la PREMIÈRE date inaccessible après START
      if (!selectingStartDate && selectedStartDate) {
        const firstInAccessible = getFirstBlockedDateAfterStart(selectedStartDate);
        if (firstInAccessible && isSameDay(date, firstInAccessible)) {
          classes.push('calendar-day--potential-checkout');
        }
      }
    } else if (isDateBlocked(date)) {
      classes.push('calendar-day--blocked');
      // Les jours bloqués manuellement ne sont JAMAIS des dates de fin potentielles
    } else if (isDayClosed(date)) {
      classes.push('calendar-day--closed');
    } else if (isDateExplicitlyAvailable(date)) {
      classes.push('calendar-day--explicitly-available');
    } else {
      classes.push('calendar-day--available');
    }
    
    return classes.join(' ');
  };

  return (
    <div className="availability-calendar">
      {/* En-tête du calendrier */}
      <div className="calendar-header">
        <button 
          className="calendar-nav-button"
          onClick={goToPreviousMonth}
          aria-label={t('sidebar.calendar.navigation.previousMonth')}
        >
          <span>‹</span>
        </button>
        
        <div className="calendar-title">
          <span>📅</span>
          <h3>{format(currentMonth, 'MMMM yyyy', { locale: dateLocale })}</h3>
        </div>
        
        <button 
          className="calendar-nav-button"
          onClick={goToNextMonth}
          aria-label={t('sidebar.calendar.navigation.nextMonth')}
        >
          <span>›</span>
        </button>
      </div>

      {/* Jours de la semaine */}
      <div className="calendar-weekdays">
        {getWeekdayNames().map((day, index) => (
          <div key={index} className="calendar-weekday">
            {day}
          </div>
        ))}
      </div>

      {/* Grille du calendrier */}
      {/* Message d'erreur de sélection (durée min/max) */}
      {selectionError && (
        <div className="calendar-selection-error" style={{
          backgroundColor: '#fff3e0',
          color: '#e65100',
          padding: '8px 12px',
          borderRadius: 6,
          fontSize: '0.85rem',
          fontWeight: 600,
          textAlign: 'center',
          marginBottom: 8,
          border: '1px solid #ffcc80'
        }}>
          ⚠️ {selectionError}
        </div>
      )}

      {/* Indicateur de contraintes de durée */}
      {(minDuration || maxDuration) && (
        <div style={{
          fontSize: '0.78rem',
          color: '#666',
          textAlign: 'center',
          marginBottom: 6
        }}>
          {minDuration && maxDuration
            ? `📏 ${minDuration} – ${maxDuration} ${durationUnitLabel}`
            : minDuration
              ? `📏 ${t('sidebar.calendar.constraints.min')} ${minDuration} ${durationUnitLabel}`
              : `📏 ${t('sidebar.calendar.constraints.max')} ${maxDuration} ${durationUnitLabel}`}
        </div>
      )}

      {loading ? (
        <div className="calendar-loading">
          <div className="loading-spinner"></div>
          <p>{t('sidebar.calendar.loading.text')}</p>
        </div>
      ) : (
        <div className="calendar-grid">
          {calendarDays.map((date, index) => {
            // Déterminer si le bouton est cliquable : START ou END avec logique différente
            const isClickable = selectingStartDate 
              ? isDateSelectable(date)              // START : doit être disponible
              : !isDateInPast(date) && !isDateBlocked(date); // END : pas dans le passé et pas owner-blocked
            
            return (
            <button
              key={index}
              className={getDateClassName(date)}
              onClick={() => handleDateClick(date)}
              onMouseEnter={() => !selectingStartDate && !isDateBlocked(date) && setHoveredDate(date)}
              onMouseLeave={() => setHoveredDate(null)}
              disabled={!isSameMonth(date, currentMonth) || !isClickable}
              title={
                isDateInPast(date) ? t('sidebar.calendar.availability.pastDate') :
                isDateBlocked(date) ? t('sidebar.calendar.availability.blocked') :
                isDateFullyBooked(date) ? t('sidebar.calendar.availability.fullyBooked') :
                isDateExplicitlyAvailable(date) ? t('sidebar.calendar.availability.explicitlyAvailable') :
                t('sidebar.calendar.availability.defaultAvailable')
              }
            >
              <span className="calendar-day-number">
                {format(date, 'd')}
              </span>
              {!isDateInPast(date) && isDateExplicitlyAvailable(date) && (
                <span className="calendar-day-indicator explicitly-available"></span>
              )}
              {!isDateInPast(date) && isDateAvailable(date) && !isDateExplicitlyAvailable(date) && (
                <span className="calendar-day-indicator available"></span>
              )}
              {!isDateInPast(date) && isDateBlocked(date) && (
                <span className="calendar-day-indicator blocked"></span>
              )}
            </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AvailabilityCalendar;
